import { useState, useEffect } from 'react';
import apiUser from '../../../apiUser';
import { fmtBRL } from '../format';

// Status em que recalcular faz sentido (mesmo criterio de edicao do ciclo no backend).
// 'registrado' (ciclo interno): só recalcula e salva — nunca envia; o backend restringe a admin.
const EDITAVEL = ['aguardando', 'contestado', 'erro', 'registrado'];

// Abas E-mail | Itens do relatorio de um ciclo. Itens carrega na hora; e-mail so quando aberto.
// "Recalcular e salvar (sem enviar)": grava o snapshot com o estado atual dos itens (via
// /ciclo/recalcular, que NAO envia e-mail), recarrega a tabela ja com o valor salvo e avisa
// o painel (onSaved) pra atualizar a lista. O reenvio ao cliente continua acao separada.
const RelatorioAbas = ({ idCiclo, status, onSaved }) => {
    const [aba, setAba] = useState('itens');
    const [snap, setSnap] = useState(null);
    const [email, setEmail] = useState(null);
    const [carregandoEmail, setCarregandoEmail] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [recalc, setRecalc] = useState(null); // { valor_anterior, valor_novo } apos salvar
    const [erroRecalc, setErroRecalc] = useState(null);

    const carregarSnapshot = () => apiUser.get('/faturamento/ciclo/snapshot', { params: { id_ciclo: idCiclo } })
        .then(({ data }) => { if (data.success) setSnap(data.dados); });

    useEffect(() => {
        setSnap(null); setEmail(null); setAba('itens'); setCarregandoEmail(false);
        setSalvando(false); setRecalc(null); setErroRecalc(null);
        carregarSnapshot().catch(() => { });
    }, [idCiclo]);

    const abrirEmail = () => {
        setAba('email');
        if (email || carregandoEmail) return;
        setCarregandoEmail(true);
        apiUser.get('/faturamento/ciclo/previewEmail', { params: { id_ciclo: idCiclo } })
            .then(({ data }) => { if (data.success) setEmail(data.dados); })
            .catch(() => { })
            .finally(() => setCarregandoEmail(false));
    };

    const recalcular = async () => {
        setAba('itens'); setErroRecalc(null); setSalvando(true);
        try {
            const { data } = await apiUser.post('/faturamento/ciclo/recalcular', { id_ciclo: idCiclo });
            if (!data.success) { setErroRecalc(data.message || 'Não foi possível recalcular.'); return; }
            setRecalc(data.dados);
            await carregarSnapshot();      // tabela ja com o snapshot salvo
            if (onSaved) onSaved();         // painel atualiza o valor na lista
        } catch { setErroRecalc('Erro ao comunicar com o servidor.'); }
        finally { setSalvando(false); }
    };

    const editavel = EDITAVEL.includes(status);
    // Itens acima do contratado (spec 2026-10-09): o valor não é cortado; precisa de aditivo.
    const pendencias = ((snap && snap.itens) || []).filter((it) => Number(it.excedente) > 0);
    const diff = recalc ? Number((recalc.valor_novo - recalc.valor_anterior).toFixed(2)) : 0;
    const tabCls = (ativa) => `px-4 py-2 text-sm font-semibold ${ativa ? 'bg-white dark:bg-gray-800 text-vox-blue-700 border-b-2 border-vox-blue-600' : 'text-gray-500'}`;

    return (
        <div className='border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden'>
            <div className='flex items-center border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900'>
                <button type='button' onClick={() => setAba('itens')} className={tabCls(aba === 'itens')}>Itens</button>
                <button type='button' onClick={abrirEmail} className={tabCls(aba === 'email')}>E-mail</button>
                {editavel && (
                    <button type='button' onClick={recalcular} disabled={salvando}
                        className='ml-auto mr-2 px-3 py-1 text-xs font-semibold rounded-lg border border-vox-blue-300 text-vox-blue-700 hover:bg-vox-blue-50 disabled:opacity-50'>
                        {salvando ? 'Recalculando…' : 'Recalcular e salvar (sem enviar)'}
                    </button>
                )}
            </div>
            {aba === 'itens' ? (
                <div className='p-3 max-h-72 overflow-auto'>
                    {recalc && (
                        <div className='mb-2 flex items-center gap-2 flex-wrap bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm'>
                            <span className='font-semibold text-green-800'>Recalculado e salvo — não enviado</span>
                            <span className='text-gray-500'>De</span><span className='font-semibold'>{fmtBRL(recalc.valor_anterior)}</span>
                            <span className='text-gray-400'>→</span>
                            <span className='text-gray-500'>Para</span><span className='font-bold text-green-800'>{fmtBRL(recalc.valor_novo)}</span>
                            {diff !== 0 && <span className={`text-xs font-semibold ${diff > 0 ? 'text-green-700' : 'text-red-700'}`}>({diff > 0 ? '+' : ''}{fmtBRL(diff)})</span>}
                        </div>
                    )}
                    {pendencias.length > 0 && (
                        <div className='mb-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm'>
                            <span className='font-semibold text-red-800'>Pendência de aditivo</span>
                            <span className='text-red-700'> — {pendencias.length} item(ns) acima da quantidade contratada (o valor não foi cortado):</span>
                            <ul className='mt-1 list-disc pl-5 text-red-700'>
                                {pendencias.map((it) => (
                                    <li key={it.id}>{String(it.descricao || '').slice(0, 80)}: +{it.excedente}{it.motivo ? ` (${it.motivo})` : ''}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {erroRecalc && <div className='mb-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700'>{erroRecalc}</div>}
                    {!snap ? <p className='text-sm text-gray-500 p-2'>Carregando…</p>
                        : !snap.itens || snap.itens.length === 0 ? <p className='text-sm text-gray-500 p-2'>Relatório indisponível.</p>
                            : (
                                <table className='w-full text-sm'>
                                    <thead>
                                        <tr className='text-left text-xs text-gray-500'>
                                            <th className='py-1'>Descrição</th>
                                            <th className='py-1 text-right'>Qtd</th>
                                            <th className='py-1 text-right'>Unit.</th>
                                            <th className='py-1 text-right'>Mês</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {snap.itens.map((it) => (
                                            <tr key={it.id} className='border-t border-gray-100 dark:border-gray-700'>
                                                <td className='py-1 pr-2'>
                                                    {it.descricao}
                                                    {it.motivo && <div className={`text-xs ${Number(it.excedente) > 0 ? 'text-red-700' : 'text-gray-500'}`}>{it.motivo}</div>}
                                                </td>
                                                <td className='py-1 text-right'>{it.quantidade_implantada}</td>
                                                <td className='py-1 text-right'>{fmtBRL(it.valor_unitario)}</td>
                                                <td className='py-1 text-right'>{fmtBRL(it.valor_mes)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className='border-t-2 border-gray-200 dark:border-gray-600 font-semibold'>
                                            <td className='py-1' colSpan={3}>Total</td>
                                            <td className='py-1 text-right'>{fmtBRL(snap.totais && snap.totais.total)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            )}
                </div>
            ) : (
                <div className='bg-white'>
                    {carregandoEmail ? <p className='text-sm text-gray-500 p-3'>Carregando o e-mail…</p>
                        : email ? <iframe title='E-mail do cliente' sandbox='' srcDoc={email.html} className='w-full' style={{ height: '420px', border: 'none' }} />
                            : <p className='text-sm text-gray-500 p-3'>E-mail indisponível.</p>}
                </div>
            )}
        </div>
    );
};

export default RelatorioAbas;
